import React, { useCallback, useState } from 'react';
import ConfirmationModal from '../ui/ConfirmationModal';
import api from '../../services/api';
import { confirmRows, openWorkOrders, reopenWorkOrders } from '../../utils/jobCompletion';

/**
 * IDI-246: the one confirm for completing or reopening jobs, used by the Job
 * page, the Jobs list's bulk "Complete Job" and the Dashboard's ⋮ "Mark
 * Complete". It lists the work orders that change; confirming calls
 * complete_job / reopen_job per job (the server closes or restores them and
 * writes the Notes entry).
 *
 * The dialog renders ONLY while open: JobDetail tests mock ConfirmationModal
 * with no default export, so a closed dialog must never mount it.
 */

export function apiErrorMessage(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

export function useJobCompletion() {
  const [state, setState] = useState(null); // { mode, jobs, resolve }
  const [busy, setBusy] = useState(false);

  // Resolves to { ok: [savedJob…], failed: [{ job, err }…] }, or null on Cancel.
  const ask = useCallback((mode, jobs) => new Promise((resolve) => {
    setState({ mode, jobs: jobs.filter(Boolean), resolve });
  }), []);

  const requestComplete = useCallback((jobs) => ask('complete', Array.isArray(jobs) ? jobs : [jobs]), [ask]);
  const requestReopen = useCallback((job) => ask('reopen', [job]), [ask]);

  const close = () => {
    state?.resolve(null);
    setState(null);
  };

  const run = async () => {
    if (!state) return;
    const { mode, jobs, resolve } = state;
    setBusy(true);
    const call = mode === 'complete' ? (id) => api.completeJob(id) : (id) => api.reopenJob(id);
    const results = await Promise.allSettled(jobs.map((j) => call(j.id)));
    setBusy(false);
    const ok = [];
    const failed = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') ok.push(r.value);
      else failed.push({ job: jobs[i], err: r.reason });
    });
    setState(null);
    resolve({ ok, failed });
  };

  const dialog = state ? (
    <JobCompletionDialog mode={state.mode} jobs={state.jobs} busy={busy} onConfirm={run} onClose={close} />
  ) : null;

  return { requestComplete, requestReopen, dialog };
}

export function JobCompletionDialog({ mode, jobs, busy, onConfirm, onClose }) {
  const complete = mode === 'complete';
  const rows = confirmRows(jobs, complete ? openWorkOrders : reopenWorkOrders);
  const title = complete
    ? (jobs.length > 1 ? `Complete ${jobs.length} jobs?` : 'Complete this job?')
    : 'Reopen this job?';
  let lead;
  if (complete) lead = rows.length ? 'These open work orders will be marked complete:' : 'This job has no open work orders.';
  else lead = rows.length ? 'These work orders will reopen:' : '';
  return (
    <ConfirmationModal
      open
      title={title}
      confirmLabel={complete ? 'Complete Job' : 'Reopen Job'}
      cancelLabel="Cancel"
      busy={busy}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      {lead && (
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{lead}</p>
      )}
      {rows.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm" data-testid="job-completion-rows">
          {rows.map((r) => <li key={r.key}>{r.label}</li>)}
        </ul>
      )}
    </ConfirmationModal>
  );
}
